import { useState, useEffect, useCallback, useRef } from 'react';
import localForage from 'localforage';

type ErrorHandler = (e?: Error) => void;

const defaultErrorHandler: ErrorHandler = (e?: Error) => {
  // eslint-disable-next-line no-console
  console.error(e);
};
/**
 * Hantera värde som sparas lokalt på webbläsaren.
 * Använder localForage som ger funktionalitet som localStorage, men väljer hur datat sparas beroende på vad som är tillgängligt.
 * Används som useState och returnerare en tuple med värde och setter. Finns även en extra funktion för att ta bort värde och en loading-flagga.
 * */
export function useLocalStorage<D>(
  /** Nycker där värdet ska sparas, kan definieras i localStorageKeys.ts */
  key: string,
  /** Värde som används innan inläsningen är klar och om värde saknas */
  initialValue: D,
  /** Körs vid fel vid läsning eller skrivning av värde */
  errorHandler?: ErrorHandler,
  /** Körs när ett värde lästs in från databasen */
  onGetValue?: (err: unknown, value: D | null) => void
) {
  const [storedValue, setStoredValue] = useState<D>(initialValue);
  const [loading, setLoading] = useState<boolean>(true);
  const _errorHandler = useRef(
    errorHandler == null ? defaultErrorHandler : errorHandler
  );

  const error = (e?: unknown) => {
    const normalizedError =
      e == null ? undefined : e instanceof Error ? e : new Error(String(e));
    _errorHandler.current(normalizedError);
  };

  useEffect(() => {
    (async function () {
      try {
        const value: D | null = await localForage.getItem<D | null>(
          key,
          onGetValue
        );
        setStoredValue(value == null ? initialValue : value);
        setLoading(false);
      } catch (e) {
        error(e);
      }
    })();
  }, [initialValue, key, onGetValue]);

  const setValue = useCallback(
    (value: D) => {
      async function set(value: any) {
        try {
          setStoredValue(value);
          await localForage.setItem(key, value);
        } catch (e) {
          error(e);
        }
      }

      set(value);
    },
    [key]
  );

  const removeValue = useCallback(() => {
    async function remove() {
      try {
        setStoredValue(initialValue);
        await localForage.removeItem(key);
      } catch (e) {
        error(e);
      }
    }

    remove();
  }, [key, initialValue]);

  return [storedValue, setValue, removeValue, loading] as const;
}
